import React, { useEffect, useRef } from 'react';
import { Package, TestTubes, LineChart, Unlock, Clock, CalendarCheck } from 'lucide-react';
import { Card } from '@/components/ui/card';

const steps = [
  {
    icon: <Package className="w-6 h-6" />,
    title: "Send Products",
    date: "By July 15th",
    description: "Ship your products to our facility for distribution"
  }, {
    icon: <TestTubes className="w-6 h-6" />,
    title: "Experiment Distribution",
    date: "2 weeks",
    description: "Products packaged and sent to selected testers"
  }, {
    icon: <LineChart className="w-6 h-6" />,
    title: "Testing Begins",
    date: "Continuous",
    description: "Participants start testing with CGM monitoring"
  }, {
    icon: <Unlock className="w-6 h-6" />,
    title: "Live Dashboard Access",
    date: "August 1st",
    description: "Access real-time data and insights"
  }
];

const HowItWorks: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const elements = entry.target.querySelectorAll('.animate-on-scroll');
          elements.forEach((el, i) => {
            setTimeout(() => {
              el.classList.add('visible');
            }, i * 150); // stagger the animations
          });
        }
      });
    }, {
      threshold: 0.1
    });

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return <section ref={sectionRef} id="how-it-works" className="section-padding bg-white">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-16 animate-on-scroll">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Batch Two Onboarding Steps
          </h2>
          <p className="text-neutral-700 text-lg">
            Get started with our service and access your data dashboard by July 15th.
          </p>
        </div>

        <div className="relative">
          {/* Timeline connector */}
          <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-neutral-200 -translate-y-[170%] z-0"></div>

          <div className="grid md:grid-cols-4 gap-10 md:gap-4 relative z-10">
            {steps.map((step, index) => <div key={index} className="flex flex-col items-center text-center animate-on-scroll">
                <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center text-white mb-4 md:mb-6 shadow-md relative z-10">
                  {step.icon}
                </div>

                <div className="px-2 py-1 bg-[#F59E0B] text-white text-xs font-semibold rounded-full mb-2">
                  {step.date}
                </div>

                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-neutral-700">{step.description}</p>
              </div>)}
          </div>
        </div>

        {/* Enhanced results timeline card */}
        <Card className="mt-12 max-w-3xl mx-auto animate-on-scroll overflow-hidden bg-gradient-to-r from-neutral-50 to-neutral-100 border border-neutral-200 shadow-sm">
          <div className="p-6">
            <div className="flex items-center justify-center mb-4">
              <CalendarCheck className="w-6 h-6 text-primary mr-2" />
              <h4 className="text-xl font-semibold">Results Timeline</h4>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0 w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                <Clock className="w-10 h-10 text-primary" />
              </div>

              <p className="text-neutral-700 text-center md:text-left">
                Once we receive your samples, you can expect early results to start trickling in, with all 10 validated results typically delivered within <span className="font-semibold">10 weeks</span>—including <span className="font-semibold">2 weeks</span> for kit preparation and <span className="font-semibold">8 weeks</span> for testing and validation.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </section>;
};

export default HowItWorks;
